<template>
  <div id="topic-wrapper" class="wdw-widget-topic" ref="topicWrapper">
    <div class="row">
      <!-- feedbackMessage -->
      <div v-if="feedbackMessage.message !== ''">
        <div :class="feedbackMessageClasses">{{ feedbackMessage.message }}</div>
      </div>
      <template v-if="showThought && topicAvailable">
        <div id="topic-dynamic" class="thought-detail-wrapper show">
          <template v-if="showDetailedThought && !showThoughtForm">
            <!-- thought-detail-->
            <thought-wrapper
              :thoughtid="openedThoughtId"
              :config="{ showMapPreview: true }"
              :actions="wrapperActions"
              @close-thought="closeThought"
              @edit-thought="editThought"
            />
          </template>
          <!-- thought-form -->
          <div v-if="showThoughtForm && canCreateThought(topic)">
            <thought-form
              :thoughtid="openedThoughtId"
              :topic="topic"
              @open-thought="openThought"
              @feedback-thought="onFeedbackFromThought"
              @close-thought="closeThought"
              @cancel-thought-form="cancelThoughtForm"
              :labelTitle="thoughtFormTitleLabel"
              :labelDescription="thoughtFormDescriptionLabel"
            />
          </div>
        </div>
      </template>
      <!-- topic -->
      <div id="topic-static" v-show="!showThought">
        <div v-if="topicAvailable && projectConfigLoaded">
          <!-- filter -->
          <div class="thought-filter">
            <thought-filter
              v-if="topicAndProjectLoaded"
              :fields="defaultFilterFields"
              :staticTopic="true"
              :attributeFields="attributeFilterFields"
              :watermark="true"
            />
          </div>
          <!-- create new thought -->
          <wdw-dynbox v-if="topic && (canCreateThought(topic) || canCreateThoughtAfterLogin(topic))">
            <div class="create-button">
              <button type="button" class="btn btn-primary btn-create-thought" @click="openCreateThoughtForm">
                {{ domainStore.translate('thought.create.btn-title') }}
              </button>
              <OverlayPanel
                v-if="!isLoggedIn"
                ref="loginOP"
                :showCloseIcon="true"
                :dismissable="true"
                appendTo="body"
                class="wdw-widgets"
              >
                <p>{{ domainStore.translate('thought.create.requires-login') }}</p>
                <Login :domainid="domainid" />
              </OverlayPanel>
            </div>
          </wdw-dynbox>
          <!-- thought-list -->
          <div v-show="!showThought" class="thought-list">
            <thought-list
              @open-thought="openThought"
              @edit-thought="editThought"
              :config="{ hideVoting: false, hideComments: false, hideCommentButton: false }"
            />
          </div>
        </div>
        <div v-else>{{ topicNotFound() }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { userStore } from '@/store/user-store';
import { domainStore } from '@/store/domain-store';
import { projectStore } from '@/store/project-store';
import { thoughtStore } from '@/store/thought-store';
import { filterStore } from '@/store/filter-store';
import useAuthentication from '@/composables/user/useAuthentication';
import { onBeforeMount, ref, watch, computed, nextTick, reactive } from 'vue';
import { useRoute } from 'vue-router';
import ThoughtFilter from '@/components/discuss/topic/ThoughtFilter.vue';
import Login from '@/components/user/Login.vue';
import OverlayPanel from 'primevue/overlaypanel';
import ThoughtList from '@/components/discuss/topic/ThoughtList.vue';
import ThoughtWrapper from '@/components/discuss/thought/Wrapper.vue';
import WdwDynbox from '@/components/general/WdwDynbox.vue';
import useThoughtFilter from '@/composables/thought/useThoughtFilter';
import useTopicConfig from '@/composables/topic/useTopicConfig';
import useHashParams from '@/composables/general/useHashParams';
import ThoughtForm from '@/components/discuss/thought/Form.vue';
import useConverter from '@/composables/general/useConverter';
import useTopicTerm from '@/composables/topic/useTopicTerm';
import useLocalCache from '@/composables/general/useLocalCache';
import useThoughtRedirect from '@/composables/thought/useThoughtRedirect';
import { isOpenedInSafari } from '@/composables/general/useUtils';

export default {
  name: 'Topic',
  props: {
    domainid: {
      type: Number,
      required: true,
    },
    topicid: {
      type: Number,
      required: true,
    },
    lang: {
      type: String,
      required: false,
      default: 'de',
    },
  },

  setup(props) {
    const { storeRedirUrlInCache } = useLocalCache();
    const { getVirtualUser } = useAuthentication();
    const { toBool } = useConverter();
    const route = useRoute();
    const { deleteHashParam, setHashParams } = useHashParams(route);

    const filterReady = ref(false);
    const { getDefaultFilterFields, getVoteRelatedFilterFields, getSortOptions, getThoughtWeightField } =
      useThoughtFilter();
    const { canCreateThought, canCreateThoughtAfterLogin } = useTopicConfig();
    const { topicLabel, topicNotFound } = useTopicTerm();

    const defaultFilterFields = ref({});
    const attributeFilterFields = ref([]);

    const feedbackMessage = reactive({
      message: '',
      type: '',
    });

    // add specific topic filter to filter fields
    defaultFilterFields.value = getDefaultFilterFields();
    defaultFilterFields.value.topicid = {
      name: topicLabel(),
      type: 'number',
      default_value: props.topicid,
      visible: false,
    };

    // refs used for behaviour
    const openedThoughtId = ref(null);
    const showThought = ref(false);
    const scrollPosition = ref(null);
    const showThoughtForm = ref(false);
    const loginOP = ref();
    const topicWrapper = ref(HTMLElement | null);
    const wrapperActions = ref({});
    const projectConfigLoaded = ref(false);

    // set props to store for initialization
    onBeforeMount(() => {
      domainStore.initDomain(props.domainid, props.lang);
      projectStore.setTopicId(props.topicid).then(topic => {
        if (topic.project_config && 'thought_list_rows' in topic.project_config) {
          thoughtStore.setRows(parseInt(topic.project_config.thought_list_rows));
        }
        projectConfigLoaded.value = true;
      });
    });

    // computed
    const user = computed(() => {
      return userStore.getState().user;
    });
    const isLoggedIn = computed(() => {
      return userStore.isLoggedIn();
    });
    const topic = computed(() => {
      return projectStore.getFetchedTopic(props.topicid);
    });
    const domain = computed(() => {
      return domainStore.getState().domain;
    });
    const project = computed(() => {
      return projectStore.getState().project;
    });
    const thoughtFormTitleLabel = computed(() => {
      return project.value
        ? project.value.thought_title_label
        : domainStore.translate('thought.create.form.title-label');
    });
    const thoughtFormDescriptionLabel = computed(() => {
      return project.value
        ? project.value.thought_content_label
        : domainStore.translate('thought.create.form.description-label');
    });
    const topicAvailable = computed(() => {
      if (topic.value) {
        return topic.value.id ? true : false;
      }
      return false;
    });
    const feedbackMessageClasses = computed(() => {
      const classes = { alert: true };
      if (feedbackMessage.type && feedbackMessage.type !== '') {
        classes['alert-' + feedbackMessage.type] = true;
      } else {
        classes['alert-info'] = true;
      }
      return classes;
    });

    const showDetailedThought = computed(() => {
      if (showThought.value && openedThoughtId.value) {
        return true;
      }
      return false;
    });
    const discuss_config = computed(() => {
      return domainStore.getState().discuss;
    });
    //Idea here is to wait for both topic and Project to be loaded
    // That way we can make sure that the filter config of the Project is also included.
    const topicAndProjectLoaded = computed(() => {
      return topic.value && project.value;
    });
    // watch on topic to setup filter values after topic was fetched
    watch(topic, (topic, oldVal) => {
      if (topic !== null && topic.id === props.topicid && topic.attributes) {
        topic.attributes.forEach(el => {
          if (toBool(el.relation_data.show_in_filter)) {
            // clone element to be able to edit it for filter
            const elCopy = JSON.parse(JSON.stringify(el));
            elCopy.filter_name = 'attribute' + el.id;
            elCopy.visible = true;
            const field = attributeFilterFields.value.find(field => {
              return field.id === el.id;
            });
            if (!field) {
              attributeFilterFields.value.push(elCopy);
            }
          }
        });

        if (!userStore.getState().authToken && process.env.VUE_APP_ENABLE_VIRTUAL_USER) {
          // try to init/get virtual user for eventual anonymous votes/comments/thoughts
          getVirtualUser()
            .then(success => {
              // console.log('initialized virtual user');
            })
            .catch(e => {
              console.log('error getting virtual user');
            });
        }
      }
    });

    // Watch isLoggedIn
    watch(isLoggedIn, (nv, ov) => {
      if (nv != ov) {
        if (nv) {
          // User is logged in
          const voteRelatedFilters = getVoteRelatedFilterFields([topic.value], nv);
          if (voteRelatedFilters.user_voted) {
            defaultFilterFields.value.user_voted = voteRelatedFilters.user_voted;
          }
        } else {
          // User is logged out
          delete defaultFilterFields.value.user_voted;
        }
      }
    });

    watch(
      () => topicAndProjectLoaded.value,
      (newVal, oldVal) => {
        if (newVal) {
          const isWeightedSupportType = topic.value.votetype == 'weighted_support';
          const sortOpts = getSortOptions({
            votetype: topic.value.votetype,
            default_sort: topic.value.default_sort,
            commentcount: topic.value.commentcount,
            new_comments: topic.value.new_comments,
          });
          defaultFilterFields.value.sort.default_value = sortOpts.default;
          defaultFilterFields.value.sort.values = sortOpts.values;

          if (project.value.config) {
            const weightField = getThoughtWeightField([topic.value], project.value.config.weighted_support_point_map);
            if (weightField != null) {
              defaultFilterFields.value.vote_weight = weightField;
            }
          }

          // If the topic has voting enabled, we need to show `user_voted` filter
          const voteRelatedFilters = getVoteRelatedFilterFields([topic.value], isLoggedIn.value);
          defaultFilterFields.value = { ...defaultFilterFields.value, ...voteRelatedFilters };

          const filterParams = {
            sort: defaultFilterFields.value.sort.default_value,
            topicid: props.topicid,
            projectid: project.value.id,
          };

          // set default values for filter
          filterStore.setDefaultValues(filterParams);
          // Update the thought filter value for filtering
          thoughtStore.updateThoughtFilter(filterParams);
          // T22381: Don't reload thoughts again here! Let ThoughtFilter reloads them since filter values can come from hash param
        }
      }
    );
    // methods
    function clearFeedback() {
      feedbackMessage.message = '';
      feedbackMessage.type = '';
    }
    function confirmClosingForm() {
      return confirm(domainStore.translate('discuss.create.form.cancel-confirm'));
    }
    function openThought(thoughtid, topicid, actions) {
      if (actions) {
        wrapperActions.value = actions;
      } else {
        wrapperActions.value = {};
      }
      showThoughtForm.value = false; // Disables the form also
      openedThoughtId.value = thoughtid;
      showThought.value = true;
      scrollPosition.value = window.pageYOffset;
      setHashParams({ thought_id: thoughtid });
      // Async call needed in order to wait for the DOM element to display
      nextTick(function () {
        // T23220: scroll to topicWrapper's top bound.
        // NOTE: smooth scrolling doesn't work if there is another widget (e.g. QuarterMap) on top of this
        // because it will often snap the widget on top into view

        // T23471: Safari on iphone has a problem with autoscroll triggering before the thought detail is loaded,
        // moving the page to the end instead of the start of the thought component
        // Here, we try to add a small delay to let the thought loads first
        // `scrollIntoView` does not recognize behavior:instant so it defaults to auto (instantly in safari)
        if (isOpenedInSafari()) {
          setTimeout(
            () => topicWrapper.value.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'auto' }),
            500
          );
        } else {
          topicWrapper.value.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'instant' });
        }
      });
    }

    function editThought(thoughtid) {
      openedThoughtId.value = thoughtid;
      showThought.value = true;
      showThoughtForm.value = true;
      scrollPosition.value = window.pageYOffset;
    }
    function closeThought(params = {}) {
      let force = false;
      if (params['force'] === true) {
        force = true;
      }
      let closeThought = true;
      if (showThoughtForm.value && !force) {
        // double check if we should close a form
        closeThought = confirmClosingForm();
      }

      if (closeThought) {
        deleteHashParam('thought_id');
        openedThoughtId.value = null;
        showThought.value = false;
        showThoughtForm.value = false;
        if (scrollPosition.value !== null) {
          nextTick(function () {
            window.scrollTo(0, scrollPosition.value);
            scrollPosition.value = null;
          });
        }
      }
      clearFeedback();
    }
    function cancelThoughtForm() {
      if (openedThoughtId.value) {
        // go back to detail view without form
        showThoughtForm.value = false;
      } else {
        // force close create form entirely (go back to list)
        closeThought({ force: true });
      }
      clearFeedback();
    }
    function openCreateThoughtForm(event) {
      //Check if user is logged in
      if (canCreateThought(topic.value)) {
        //Show create thought when user is logged in
        showThought.value = true;
        showThoughtForm.value = true;
        openedThoughtId.value = null;
        scrollPosition.value = window.pageYOffset;
        clearFeedback();
      } else if (canCreateThoughtAfterLogin(topic.value)) {
        loginOP.value.show(event);
        storeRedirUrlInCache('create-thought');
      } else {
        console.error('error: cannot create thought, but button visible/clicked');
      }
    }
    function onFeedbackFromThought(feedback) {
      //new thought was created, go back to list view
      if (feedback.newThought || !openedThoughtId.value) {
        closeThought({ force: true });
      }
      //thought was edited, go back to thought-detail
      if (!feedback.newThought && openedThoughtId.value) {
        openThought(openedThoughtId.value);
      }

      feedbackMessage.message = feedback.message;
      feedbackMessage.type = feedback.type;
    }

    useThoughtRedirect(openThought, openCreateThoughtForm);

    // check hashparams here
    watch(route, () => {
      const thought_id = parseInt(route.query.thought_id);
      if (thought_id && openedThoughtId.value != thought_id) {
        openThought(thought_id);
      }
    });

    return {
      // refs
      domainStore,
      attributeFilterFields,
      defaultFilterFields,
      openedThoughtId,
      showThoughtForm,
      showThought,
      loginOP,
      topicWrapper,
      wrapperActions,
      projectConfigLoaded,

      // computed vars
      user,
      isLoggedIn,
      topic,
      topicAvailable,
      showDetailedThought,
      discuss_config,
      feedbackMessageClasses,
      thoughtFormTitleLabel,
      thoughtFormDescriptionLabel,
      topicAndProjectLoaded,

      // methods
      canCreateThought,
      canCreateThoughtAfterLogin,
      openThought,
      closeThought,
      editThought,
      cancelThoughtForm,
      openCreateThoughtForm,
      onFeedbackFromThought,
      topicNotFound,

      // Vars
      feedbackMessage,
    };
  },

  components: {
    OverlayPanel,
    Login,
    ThoughtFilter,
    ThoughtList,
    WdwDynbox,
    ThoughtWrapper,
    ThoughtForm,
  },
};
</script>
<style scoped>
#topic-wrapper {
  scroll-margin-top: 100px;
  /* required so scroll-margin works in chrome */
  overflow: initial;
}
</style>
